import * as React from "react"

import { Input } from "./input"
import { cn } from "../../lib/utils"

type FloatingLabelInputProps = Omit<
  React.ComponentPropsWithoutRef<typeof Input>,
  "placeholder"
> & {
  label: React.ReactNode
  invalid?: boolean
  errorText?: React.ReactNode
  disableAnimation?: boolean
  forceFloat?: boolean
}

const FloatingLabelInput = React.forwardRef<HTMLInputElement, FloatingLabelInputProps>(
  (
    {
      id,
      label,
      required,
      invalid,
      errorText,
      disableAnimation,
      forceFloat,
      className,
      disabled,
      ...props
    },
    ref
  ) => {
    const generatedId = React.useId()
    const inputId = id ?? `floating-${generatedId}`
    const errorId = errorText ? `${inputId}-error` : undefined
    const isRtl = props.dir === "rtl"

    return (
      <div className="w-full">
        <div className="relative" data-invalid={invalid ? "true" : "false"}>
          <Input
            ref={ref}
            id={inputId}
            required={required}
            disabled={disabled}
            aria-invalid={invalid ? true : undefined}
            aria-errormessage={invalid && errorId ? errorId : undefined}
            placeholder=" "
            className={cn(
              "peer box-border h-10 w-full rounded-none border-0 border-b border-lupa-muted bg-lupa-surface px-3 pb-0.5 pt-3.5 font-ploni text-[18px] leading-none text-lupa-text shadow-none ring-0 placeholder:text-transparent focus-visible:border-b-lupa-brand focus-visible:ring-0 disabled:cursor-not-allowed disabled:text-lupa-muted",
              invalid && "border-b-lupa-error",
              className
            )}
            {...props}
          />
          <label
            htmlFor={inputId}
            style={{ insetInlineStart: "0.75rem" }}
            className={cn(
              "absolute top-1.5 z-10 select-none bg-transparent px-0 text-xs leading-none text-lupa-text text-start font-ploni",
              isRtl ? "origin-top-right" : "origin-top-left",
              "translate-y-0 scale-100",
              "peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-placeholder-shown:text-[14px] peer-placeholder-shown:pointer-events-none",
              "peer-focus:top-1.5 peer-focus:translate-y-0 peer-focus:scale-100 peer-focus:text-lupa-text",
              "peer-disabled:text-lupa-muted",
              invalid && "text-lupa-error peer-focus:text-lupa-error",
              disableAnimation ? "transition-none" : "transition-all duration-200 ease-out",
              forceFloat &&
                "!top-1.5 !translate-y-0 !scale-100 peer-placeholder-shown:!top-1.5 peer-placeholder-shown:!translate-y-0 peer-placeholder-shown:!scale-100"
            )}
          >
            {label}
            {required && (
              <span className={cn("ms-0.5", disabled ? "text-lupa-muted" : invalid ? "text-lupa-error" : "text-lupa-text")} aria-hidden="true">
                *
              </span>
            )}
          </label>
        </div>
        <p
          id={errorId}
          className={cn(
            "mt-1 min-h-[18px] text-start text-[12px]",
            invalid ? "text-lupa-error" : "invisible"
          )}
          role={invalid && errorText ? "alert" : undefined}
        >
          {invalid && errorText ? errorText : "\u00A0"}
        </p>
      </div>
    )
  }
)

FloatingLabelInput.displayName = "FloatingLabelInput"

export { FloatingLabelInput }
